import * as React from 'react';
import { Edit3, History, Plus, RefreshCw, UserCheck, UserX } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableFilterTrigger,
  DashboardTableSearchInput,
  DashboardTableStatusTone,
  buildDashboardDataTableColumns,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  dashboardTableSelectTriggerClassName,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useShigoSession } from '../../../../../lib/useShigoSession';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { ShigoPermission } from '../../../../auth/permissions';
import { formatShigoRole } from '../../../../auth/roleLabels';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { ChangelogDialog } from '../../../../changelog/components/ChangelogDialog';
import {
  useAdminUserMutations,
  useAdminUsersQuery,
  useAdminUserStatusCountsQuery,
} from '../../hooks';
import {
  AdminUsersContext,
  AdminUserSortDirection,
  AdminUserSortProperty,
  AdminUserStatus,
  adminUsersPageSizes,
  adminUserFilterRoles,
  isAdminUserSortProperty,
  statusForAdminUser,
  type AdminUser,
  type AdminUserMembership,
} from '../../model';
import { useUsersRouteFilters } from '../../useUsersRouteFilters';
import { UserDialog } from '../UserDialog/UserDialog';

const statusOptions = Object.values(AdminUserStatus);

export function UsersTable({ context }: { context: AdminUsersContext }) {
  const { session } = useShigoSession();
  const { can } = useShigoPermissions();
  const { filter, setPagination, setRole, setSearch, setSort, setStatuses } =
    useUsersRouteFilters();
  const query = useAdminUsersQuery(context, filter);
  const counts = useAdminUserStatusCountsQuery(context);
  const mutations = useAdminUserMutations(context);
  const [editingUser, setEditingUser] = React.useState<AdminUser | null | undefined>();
  const [changelogUsername, setChangelogUsername] = React.useState<string>();
  const [statusPendingIds, setStatusPendingIds] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const search = useDebouncedValue(filter.searchTerm, setSearch, 650);

  const canCreate = can(ShigoPermission.AdminUsersWrite);
  const canEdit = can(ShigoPermission.AdminUsersEdit);
  const canChangeStatus = can(ShigoPermission.AdminUsersDelete);
  const canViewChangelog = can(ShigoPermission.AdminChangelogRead);
  const currentUsername = session?.user.username ?? session?.user.email;
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<AdminUser>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('users.name'),
          headerClassName: 'w-64 pl-4',
          className: 'w-64 pl-4 font-medium',
          value: (user) => user.name,
          sortable: true,
          sortKey: AdminUserSortProperty.Name,
        },
        {
          id: 'network',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('users.network'),
          headerClassName: 'w-48',
          className: 'w-48',
          cell: (user) => {
            const networks = networkNamesForUser(user).join(', ');
            return <TruncatedUserTableValue value={networks} />;
          },
          hidden: context !== AdminUsersContext.SuperAdmin,
        },
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('users.status'),
          headerClassName: 'w-28',
          className: 'w-28',
          label: (user) =>
            dashboardT(
              statusForAdminUser(user) === AdminUserStatus.Active
                ? 'users.active'
                : 'users.archived',
            ),
          tone: (user) =>
            statusForAdminUser(user) === AdminUserStatus.Active
              ? DashboardTableStatusTone.Success
              : DashboardTableStatusTone.Neutral,
        },
        {
          id: 'username',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('users.username'),
          headerClassName: 'w-64',
          className: 'w-64',
          value: (user) => user.username ?? user.email,
          sortable: true,
          sortKey: AdminUserSortProperty.Username,
        },
        {
          id: 'roles',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('users.roles'),
          headerClassName: 'w-56',
          className: 'w-56',
          cell: (user) => {
            const roles = rolesForUser(user, context).map(formatShigoRole).join(', ');
            return <TruncatedUserTableValue value={roles} />;
          },
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          cell: (user) => {
            const username = user.username ?? user.email;
            const pending = statusPendingIds.has(username);
            const enabled = user.enabled !== false;
            return (
              <DashboardTableRowActions
                label={dashboardT('users.actionsFor', { name: user.name })}
                pending={pending}
              >
                {canEdit ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      setEditingUser(user);
                    }}
                  >
                    <Edit3 className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('users.edit')}
                  </DashboardTableRowActionItem>
                ) : null}
                {canChangeStatus && username !== currentUsername ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      setStatusPendingIds((current) => new Set(current).add(username));
                      void mutations.setEnabled
                        .mutateAsync({ username, enabled: !enabled })
                        .then(() => {
                          toast.success(
                            dashboardT(enabled ? 'users.disabledSuccess' : 'users.enabledSuccess'),
                          );
                        })
                        .catch((error: unknown) => {
                          showRequestError(error, dashboardT('users.statusUpdateError'));
                        })
                        .finally(() => {
                          setStatusPendingIds((current) => {
                            const next = new Set(current);
                            next.delete(username);
                            return next;
                          });
                        });
                    }}
                  >
                    {enabled ? (
                      <UserX className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <UserCheck className="h-4 w-4" aria-hidden="true" />
                    )}
                    {dashboardT(enabled ? 'users.disable' : 'users.enable')}
                  </DashboardTableRowActionItem>
                ) : null}
                {canViewChangelog ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      setChangelogUsername(username);
                    }}
                  >
                    <History className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('users.changelog')}
                  </DashboardTableRowActionItem>
                ) : null}
              </DashboardTableRowActions>
            );
          },
        },
      ]),
    [
      canChangeStatus,
      canEdit,
      canViewChangelog,
      context,
      currentUsername,
      mutations.setEnabled,
      statusPendingIds,
    ],
  );

  const selectedStatuses = new Set(filter.statuses);
  return (
    <main className="page-gutter min-w-0 py-6">
      <DashboardDataTable
        title={dashboardT('users.title')}
        tableLabel={dashboardT('users.title')}
        columns={columns}
        items={filter.statuses.length ? (query.data?.content ?? []) : []}
        getRowKey={(user) => user.id ?? user.username ?? user.email}
        loading={query.isFetching}
        loadingMessage={dashboardT('users.loading')}
        emptyMessage={dashboardT('users.empty')}
        errorMessage={query.isError ? dashboardT('users.loadError') : undefined}
        minWidthClassName="min-w-[1040px]"
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === AdminUserSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (sort) => {
            if (!sort) {
              setSort(null, filter.sortDirection);
              return;
            }
            if (!isAdminUserSortProperty(sort.key)) return;
            setSort(
              sort.key,
              sort.direction === DashboardDataTableSortDirection.Asc
                ? AdminUserSortDirection.Asc
                : AdminUserSortDirection.Desc,
            );
          },
        }}
        toolbar={
          <>
            <DashboardTableSearchInput
              value={search.draft}
              aria-label={dashboardT('users.search')}
              placeholder={dashboardT('users.search')}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <DashboardTableFilterTrigger
                  className="w-44"
                  label={resolveStatusFilterLabel(filter.statuses)}
                  aria-label={dashboardT('users.statusFilter')}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {statusOptions.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.has(status)}
                    onCheckedChange={(checked) => {
                      setStatuses(
                        checked
                          ? [...filter.statuses, status]
                          : filter.statuses.filter((current) => current !== status),
                      );
                    }}
                  >
                    <span className="flex min-w-0 flex-1 justify-between gap-3">
                      <span>
                        {dashboardT(
                          status === AdminUserStatus.Active ? 'users.active' : 'users.archived',
                        )}
                      </span>
                      {counts.data ? (
                        <span className="text-muted-foreground">{counts.data[status]}</span>
                      ) : null}
                    </span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Select
              value={filter.role || 'all'}
              onValueChange={(value) => {
                setRole(value === 'all' ? '' : value);
              }}
            >
              <SelectTrigger
                className={dashboardTableSelectTriggerClassName('w-48')}
                aria-label={dashboardT('users.roleFilter')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{dashboardT('users.allRoles')}</SelectItem>
                {adminUserFilterRoles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {formatShigoRole(role)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              aria-label={dashboardT('users.refresh')}
              onClick={() => {
                void query.refetch();
                if (context === AdminUsersContext.SuperAdmin) void counts.refetch();
              }}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            </Button>
            {canCreate ? (
              <Button
                onClick={() => {
                  setEditingUser(null);
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {dashboardT('users.create')}
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: filter.statuses.length ? (query.data?.totalElements ?? 0) : 0,
          },
          pageSizes: adminUsersPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
      />
      {editingUser !== undefined ? (
        <UserDialog
          context={context}
          user={editingUser}
          onClose={() => {
            setEditingUser(undefined);
          }}
        />
      ) : null}
      {changelogUsername ? (
        <ChangelogDialog
          key={changelogUsername}
          title={dashboardT('users.changelogTitle', { username: changelogUsername })}
          description={dashboardT('users.changelogDescription')}
          scope={{ createdBy: changelogUsername }}
          onClose={() => {
            setChangelogUsername(undefined);
          }}
        />
      ) : null}
    </main>
  );
}

function networkNamesForUser(user: AdminUser) {
  const memberships = membershipsForDisplay(user);
  if (memberships.length > 0) {
    return [...new Set(memberships.map((membership) => membership.networkName))];
  }
  return user.network ? [user.network.name] : [];
}

function rolesForUser(user: AdminUser, context: AdminUsersContext) {
  const memberships = membershipsForDisplay(user);
  if (context === AdminUsersContext.SuperAdmin && memberships.length > 0) {
    return [...new Set(memberships.flatMap((membership) => membership.roles))];
  }
  return user.roles;
}

function membershipsForDisplay(user: AdminUser): AdminUserMembership[] {
  return Array.isArray(user.memberships) ? user.memberships : [];
}

function TruncatedUserTableValue({ value }: { value: string }) {
  const displayValue = value || '-';
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span className="block truncate">{displayValue}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-80">{displayValue}</TooltipContent>
    </Tooltip>
  );
}

function resolveStatusFilterLabel(statuses: AdminUserStatus[]) {
  if (statuses.length === statusOptions.length) return dashboardT('users.allStatuses');
  if (statuses.length === 0) return dashboardT('users.noStatuses');
  return statuses
    .map((status) =>
      dashboardT(status === AdminUserStatus.Active ? 'users.active' : 'users.archived'),
    )
    .join(', ');
}
